import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Select2Data, Select2Option } from 'ng-select2-component';
import { ParticipantesService } from './tribunal.service';
import { UsuarioService } from '../usuarios/usuarios.service';
import { Tribunal } from './tribunal.model';
import { Usuario } from '../usuarios/usuarios.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { formatDateString, getCurrentLanguage } from '../core/helpers/utils';
import { CasosService } from '../casos/casos.service';
import { AuthenticationService } from '../core/service/auth.service';

// Interfaz extendida para TribunalConDetalles
interface TribunalConDetalles extends Participante {
  nombre?: string;
  apellidos?: string;
  arbitro_rol?: string; // Propiedades adicionales para tribunal
  arbitro_sistema_designacion?: string;
  enviar_email_subida_archivo?: boolean;
  enviar_email_hito?: boolean;
  validado?: boolean;
}

interface Participante {
  id: string;
  id_usuario: string;
  tipo: string;
  fecha_designacion: string | null;
  fecha_designacion_formateada?: string;
  enviar_email_subida_archivo?: boolean;
  estado: string;
  usuario?: Usuario; // Puede estar undefined si no tiene usuario asociado

}


@Component({
  selector: 'app-gestion-tribunal',
  templateUrl: './gestion-tribunal.component.html',
  styleUrls: ['./gestion-tribunal.component.scss']
})
export class GestionTribunalComponent implements OnInit {

  @Output() refreshCase = new EventEmitter<void>();

  usuarios: Select2Data = [];
  usuariosList: Usuario[] = [];
  tribunalesList: TribunalConDetalles[] = [];
  avatarUrls: { [key: string]: SafeUrl } = {}; // Almacena las URLs de los avatares

  model: Tribunal = {
    id_usuario: '',
    tipo: '',
    fecha_designacion: undefined,
    arbitro_rol: '',
    arbitro_sistema_designacion: '',
    enviar_email_asociacion: undefined,
    estado: ''
  };

  altaArbitroFormSubmitted: boolean = false;
  nuevoArbitro: Tribunal = {
    tipo: '',
    fecha_designacion: undefined,
    arbitro_rol: '',
    arbitro_sistema_designacion: '',
    estado: '',
    nombre: '',
    apellidos: '',
    email: '',
    enviar_email_validacion: undefined,
    enviar_email_asociacion: undefined
  };


  loaded = false;
  error = '';
  tribunalesLoaded = false; // Controla la visibilidad del div de tribunales
  isLoading = true; // Controla la visibilidad del spinner
  estadoExpediente: string | undefined;
  disableOptions: boolean = true;

  casoCerrado: boolean = true;
  cuantiaReconvencion: number | null | undefined;

  altaArbitro: boolean = false; // Controla si se muestra la sección de alta de árbitro
  emailExiste: boolean = false; // Variable para marcar si el correo existe
  validatingEmail: boolean = false; // Indica si la validación está en curso
  showConfirmDeleteModal: boolean = false;
  tribunalToDelete: TribunalConDetalles | null = null;

  constructor(
    private participantesService: ParticipantesService,
    private usuarioService: UsuarioService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private casoService: CasosService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      this.authService.currentUser().subscribe(user => {
        if (user?.nivel_acceso) {
          // Si soy admin no deshabilito opciones
          if (user.nivel_acceso == "ADMIN") {
            this.disableOptions = false;
            this.loadUsuarios();
          }
          //else
          //  this.loaded = true;
        }
      }, error => {
        console.error('Error al obtener el usuario actual:', error);
        // Maneja el error, por ejemplo, mostrando un mensaje al usuario.
      });
      this.loadTribunales();
    }
  }


  loadUsuarios(): void {
    //console.log("Cargo usuarios")
    this.usuarioService.getUsuarios('es_arbitro=true&activo=true').subscribe({ // Eliminamos la Validacion de usuarios &validado=true
      next: (data) => {
        // Cargo los usuarios
        this.usuariosList = data;
        this.usuarios = this.usuariosList.map((user): Select2Option => ({
          value: user.id || '', // Asegúrate de que el valor no sea undefined
          label: `${user.nombre} ${user.apellidos} - ${user.email} ${!user.validado ? '(No validado)' : ''}` + (user.institucion ? ` - ${user.institucion}` : '') // Mostrar institucion solo si no es null
        }));
        this.loaded = true;
      },
      error: (error) => {
        console.error('Error loading usuarios:', error);
        this.error = error;
        this.loaded = false;
      }
    });

  }


  loadTribunales(): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      this.casoService.getCaso(id_expediente).subscribe({
        next: (casoData) => {
          this.estadoExpediente = casoData.estado;
          this.cuantiaReconvencion = casoData.cuantia_reconvencion;
          this.casoCerrado = this.isCaseClosed();
          this.loaded = true;
          const participantes: Participante[] = casoData.participantes;

          // Filtrar y ordenar los tribunales
          this.tribunalesList = participantes
            .filter(participante => participante.tipo === 'TRIBUNAL' || participante.tipo === 'SECRETARIO_ADMINISTRATIVO' || participante.tipo === 'EQUIPO_TRIBUNAL')
            .map((participante): TribunalConDetalles => {
              const tribunalConDetalles: TribunalConDetalles = { ...participante };

              // Si existe un usuario asociado, rellenamos nombre y apellidos
              if (participante.usuario) {
                tribunalConDetalles.nombre = participante.usuario.nombre;
                tribunalConDetalles.apellidos = participante.usuario.apellidos;
              }

              return tribunalConDetalles;
            })
            .sort((a, b) => {
              if (a.estado === 'SUSTITUIDO' && b.estado !== 'SUSTITUIDO') return 1;
              if (a.estado !== 'SUSTITUIDO' && b.estado === 'SUSTITUIDO') return -1;
              return 0;
            });

          if (this.tribunalesList.length === 0) {
            this.tribunalesLoaded = true;
            this.isLoading = false;
            return;
          }

          let avatarLoadCount = 0;
          this.tribunalesList.forEach((tribunal) => {
            const currentLanguage = getCurrentLanguage();
            tribunal.fecha_designacion_formateada = tribunal.fecha_designacion
              ? formatDateString(tribunal.fecha_designacion, currentLanguage)
              : undefined;

            if (tribunal.id_usuario) {
              this.usuarioService.getAvatar(tribunal.id_usuario).subscribe(avatarBlob => {
                if (avatarBlob) {
                  const blobUrl = URL.createObjectURL(avatarBlob);
                  this.avatarUrls[tribunal.id_usuario] = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
                }
                avatarLoadCount++;
                if (avatarLoadCount === this.tribunalesList.length) {
                  this.tribunalesLoaded = true;
                  this.isLoading = false;
                }
              });
            } else {
              avatarLoadCount++;
              if (avatarLoadCount === this.tribunalesList.length) {
                this.tribunalesLoaded = true;
                this.isLoading = false;
              }
            }
          });
        },
        error: (error) => {
          console.error('Error loading caso:', error);
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }



  isCaseClosed(): boolean {
    const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
    return estadosCerrados.includes(this.estadoExpediente ?? '');
  }


  onSubmit(form: NgForm): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      const newTribunal: Tribunal = {
        id_expediente: id_expediente,
        id_usuario: form.value.id_usuario,
        tipo: form.value.tipo,
        fecha_designacion: form.value.fecha_designacion,
        estado: 'ACTIVO',
        enviar_email_asociacion: form.value.enviar_email_asociacion === 'true'
      };

      // Si no es SECRETARIO_ADMINISTRATIVO, añadimos arbitro_rol y arbitro_sistema_designacion
      if (form.value.tipo !== 'SECRETARIO_ADMINISTRATIVO') {
        newTribunal.arbitro_rol = form.value.arbitro_rol;
        newTribunal.arbitro_sistema_designacion = form.value.arbitro_sistema_designacion;
      }


      this.isLoading = true; // Show spinner on submit
      this.participantesService.newTribunal(newTribunal).subscribe({
        next: () => {
          this.model = { id_usuario: '', tipo: '', fecha_designacion: undefined, arbitro_rol: '', arbitro_sistema_designacion: '', estado: 'ACTIVO', enviar_email_asociacion: undefined }; // Reset form model
          this.loadTribunales(); // Refresh the tribunales list
          form.resetForm(); // Reset form validations
          this.refreshCase.emit();
        },
        error: (error) => {
          //console.error('Error creating new tribunal:', error);
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }

  onRegistrarArbitro(form: NgForm): void {
    // Verificar si el email está siendo validado actualmente
    if (this.validatingEmail) {
      return; // Evitar que se registre mientras se valida
    }

    const email = form.value.email?.trim();
    if (email) {
      this.validatingEmail = true; // Comenzar validación del email

      this.participantesService.checkEmail(email).subscribe({
        next: (response) => {
          this.emailExiste = response; // Actualizar si el correo existe
          this.validatingEmail = false; // Validación finalizada

          if (this.emailExiste) {
            // Si el correo existe, no permitir continuar
            return;
          }

          // Proceder con el registro si el correo no existe
          this.registrarArbitro(form);
        },
        error: () => {
          this.emailExiste = false;
          this.validatingEmail = false;
        }
      });
    } else {
      this.emailExiste = true; // Email no válido o vacío
      return; // No proceder si no hay un correo válido
    }
  }

  private registrarArbitro(form: NgForm): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      const newTribunal: Tribunal = {
        id_expediente: id_expediente,
        tipo: form.value.tipo,
        fecha_designacion: form.value.fecha_designacion,
        estado: 'ACTIVO',
        nombre: form.value.nombre,
        apellidos: form.value.apellidos,
        email: form.value.email,
        enviar_email_validacion: form.value.enviar_email_validacion === 'true',
        enviar_email_asociacion: form.value.enviar_email_asociacion === 'true'
      };

      // Si no es SECRETARIO_ADMINISTRATIVO, añadir arbitro_rol y arbitro_sistema_designacion
      if (form.value.tipo !== 'SECRETARIO_ADMINISTRATIVO') {
        newTribunal.arbitro_rol = form.value.arbitro_rol;
        newTribunal.arbitro_sistema_designacion = form.value.arbitro_sistema_designacion;
      }

      this.isLoading = true; // Mostrar spinner al enviar
      this.participantesService.newTribunal(newTribunal).subscribe({
        next: () => {
          this.nuevoArbitro = {
            id_usuario: '', tipo: '', fecha_designacion: undefined, arbitro_rol: '', arbitro_sistema_designacion: '', estado: 'ACTIVO',
            nombre: '', apellidos: '', email: '', enviar_email_validacion: undefined, enviar_email_asociacion: undefined
          }; // Reiniciar modelo del formulario
          this.altaArbitro = false;
          this.loadUsuarios();
          this.loadTribunales(); // Refrescar la lista de tribunales
          form.resetForm(); // Reiniciar validaciones del formulario
          this.refreshCase.emit();
        },
        error: (error) => {
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }


  toggleAltaArbitro(): void {
    this.nuevoArbitro = { estado: '', nombre: '', apellidos: '', email: '', tipo: '', fecha_designacion: undefined, arbitro_rol: '', arbitro_sistema_designacion: '', enviar_email_validacion: undefined, enviar_email_asociacion: undefined }; // Resetear modelo de alta
    this.altaArbitroFormSubmitted = false;
  }


  openDeleteModal(tribunal: TribunalConDetalles): void {
    this.tribunalToDelete = tribunal;
    this.showConfirmDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showConfirmDeleteModal = false;
    this.tribunalToDelete = null;
  }

  confirmDelete(): void {
    if (this.tribunalToDelete?.id) {
      this.deleteTribunal(this.tribunalToDelete.id, true);
    }
  }


  deleteTribunal(id: string | undefined, closeModalOnSuccess: boolean = false): void {
    if (id) {
      this.isLoading = true; // Show spinner on delete
      this.participantesService.deleteTribunal(id).subscribe({
        next: () => {
          if (closeModalOnSuccess) {
            this.closeDeleteModal();
          }
          this.loadTribunales(); // Refresh the tribunales list
          this.refreshCase.emit();
        },
        error: (error) => {
          //console.error('Error deleting tribunal:', error);
          this.loadTribunales(); // Refresh the tribunales list
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }

  markAsSustituido(id: string | undefined): void {
    if (id) {
      const tribunalToUpdate = this.tribunalesList.find(tribunal => tribunal.id === id);
      if (tribunalToUpdate) {
        tribunalToUpdate.estado = 'SUSTITUIDO';

        // Verifica si el tipo es SECRETARIO_ADMINISTRATIVO y elimina arbitro_rol y arbitro_sistema_designacion si aplica
        if (tribunalToUpdate.tipo === 'SECRETARIO_ADMINISTRATIVO') {
          delete tribunalToUpdate.arbitro_rol;
          delete tribunalToUpdate.arbitro_sistema_designacion;
        }

        // Crear un objeto limpio de tipo Tribunal para la actualización, transformando null en undefined
        const tribunalUpdateData: Tribunal = {
          id_usuario: tribunalToUpdate.id_usuario,
          tipo: tribunalToUpdate.tipo,
          fecha_designacion: tribunalToUpdate.fecha_designacion ?? undefined, // Transforma null en undefined
          estado: tribunalToUpdate.estado,
          arbitro_rol: tribunalToUpdate.arbitro_rol, // Solo si no es SECRETARIO_ADMINISTRATIVO
          arbitro_sistema_designacion: tribunalToUpdate.arbitro_sistema_designacion // Solo si no es SECRETARIO_ADMINISTRATIVO
        };

        this.participantesService.updateTribunal(id, tribunalUpdateData).subscribe({
          next: () => {
            this.loadTribunales(); // Recargar lista después de la actualización
            this.refreshCase.emit();
          },
          error: (error) => {
            console.error('Error updating tribunal:', error);
            this.error = error;
            this.loadTribunales();
          }
        });
      }
    }
  }

  onSwitchChange(id: string): void {
    const switchElement = document.getElementById(`switch_${id}`) as HTMLInputElement;
    if (switchElement) {
      const isChecked = switchElement.checked; // Estado del switch

      // Encuentra el tribunal correspondiente en la lista
      const tribunalToUpdate = this.tribunalesList.find(tribunal => tribunal.id === id);
      if (tribunalToUpdate) {
        // Actualiza el campo enviar_email_subida_archivo
        tribunalToUpdate.enviar_email_subida_archivo = isChecked;

        // Crear el objeto para la actualización
        const tribunalUpdateData: Tribunal = {
          id_usuario: tribunalToUpdate.id_usuario,
          tipo: tribunalToUpdate.tipo,
          fecha_designacion: tribunalToUpdate.fecha_designacion ?? undefined,
          estado: tribunalToUpdate.estado,
          arbitro_rol: tribunalToUpdate.arbitro_rol, // Mantener si aplica
          arbitro_sistema_designacion: tribunalToUpdate.arbitro_sistema_designacion, // Mantener si aplica
          enviar_email_subida_archivo: isChecked // Nuevo valor del switch
        };

        // Llamar al servicio para actualizar en la base de datos
        this.participantesService.updateTribunal(id, tribunalUpdateData).subscribe({
          next: () => {
            //console.log(`Tribunal con ID ${id} actualizado con éxito. Estado enviar_email_subida_archivo: ${isChecked}`);
            // Puedes emitir eventos o recargar los datos si es necesario
            //this.refreshCase.emit();
          },
          error: (error) => {
            //console.error('Error updating tribunal:', error);
            this.error = error;
            this.loadTribunales();
          }
        });
      } else {
        console.error(`No se encontró el tribunal con ID ${id} en la lista.`);
      }
    } else {
      console.error(`No se encontró el switch con ID: switch_${id}`);
    }
  }

  convertirArbitroRol(arbitroRol: string | undefined, cuantiaReconvencion?: string | number | null): string {
    const hasReconvencion = Number(cuantiaReconvencion ?? 0) > 0;
    if (!arbitroRol) {
      return '';
    }

    const language = getCurrentLanguage();
    const rolesMap: { [key: string]: string } = language === 'en'
      ? {
        'PRESIDENTE': 'PRESIDENT',
        'COARBITRO_1': hasReconvencion ? 'CO-ARBITRATOR CLAIMANT COUNTER-RESPONDENT' : 'CO-ARBITRATOR CLAIMANT',
        'COARBITRO_2': hasReconvencion ? 'CO-ARBITRATOR RESPONDENT COUNTER-CLAIMANT' : 'CO-ARBITRATOR RESPONDENT',
        'ARBITRO_UNICO': 'SOLE ARBITRATOR',
        'ARBITRO_EMERGENCIA': 'EMERGENCY ARBITRATOR'
      }
      : {
        'PRESIDENTE': 'PRESIDENTE',
        'COARBITRO_1': hasReconvencion ? 'CO-ÁRBITRO DEMANDANTE RECONVENIDO' : 'CO-ÁRBITRO DEMANDANTE',
        'COARBITRO_2': hasReconvencion ? 'CO-ÁRBITRO DEMANDADO RECONVINIENTE' : 'CO-ÁRBITRO DEMANDADO',
        'ARBITRO_UNICO': 'ÁRBITRO ÚNICO',
        'ARBITRO_EMERGENCIA': 'ÁRBITRO DE EMERGENCIA'
      };
    return rolesMap[arbitroRol] || (language === 'en' ? arbitroRol : '');
  }

  convertirSistemasDesignacion(sistemaDesignacion: string | undefined): string {
    if (!sistemaDesignacion) {
      return '';
    }

    const language = getCurrentLanguage();
    const sistemaDesignacionMap: { [key: string]: string } = language === 'en'
      ? {
        'PARTES': 'PARTIES',
        'COARBITROS': 'CO-ARBITRATORS',
        'DESIGNACION_DIRECTA': 'DIRECT APPOINTMENT',
        'DESIGNACION_LISTA': 'LIST APPOINTMENT'
      }
      : {
        'PARTES': 'PARTES',
        'COARBITROS': 'CO-ÁRBITROS',
        'DESIGNACION_DIRECTA': 'DESIGNACIÓN DIRECTA',
        'DESIGNACION_LISTA': 'DESIGNACIÓN POR LISTA'
      };
    return sistemaDesignacionMap[sistemaDesignacion] || (language === 'en' ? sistemaDesignacion : '');
  }

  comprobarEmail(email: string): void {
    this.participantesService.checkEmail(email).subscribe({
      next: (response) => {
        return response;
      },
      error: (error) => {
        this.error = error;
        return true;
      }
    });
  }

  onEmailBlur(email: string): void {
    if (email && email.trim() !== '') {
      this.validatingEmail = true; // Indica que la validación está en curso
      this.participantesService.checkEmail(email).subscribe({
        next: (response) => {
          this.emailExiste = response; // Actualiza si el correo existe
          this.validatingEmail = false; // Finaliza la validación
        },
        error: () => {
          this.emailExiste = false; // En caso de error, asumir que no existe
          this.validatingEmail = false; // Finaliza la validación
        }
      });
    } else {
      this.emailExiste = false; // Reinicia el estado si el email está vacío
      this.validatingEmail = false; // No está validando
    }
  }

  convertirArbitroTipo(arbitroTipo: string | undefined): string {
    if (!arbitroTipo) {
      return '';
    }

    const language = getCurrentLanguage();
    const tipoMap: { [key: string]: string } = language === 'en'
      ? {
        'TRIBUNAL': '',
        'SECRETARIO_ADMINISTRATIVO': 'ADMINISTRATIVE SECRETARY',
        'EQUIPO_TRIBUNAL': 'TRIBUNAL TEAM'
      }
      : {
        'TRIBUNAL': '',
        'SECRETARIO_ADMINISTRATIVO': 'SECRETARIO ADMINISTRATIVO',
        'EQUIPO_TRIBUNAL': 'EQUIPO TRIBUNAL'
      };
    return tipoMap[arbitroTipo] || (language === 'en' ? arbitroTipo : '');
  }

  onSwitchChangeHitos(id: string): void {
    const switchElement = document.getElementById(`switchHito_${id}`) as HTMLInputElement;
    if (switchElement) {
      const isChecked = switchElement.checked; // Estado del switch
      // Encuentra el tribunal correspondiente en la lista
      const tribunalToUpdate = this.tribunalesList.find(institucion => institucion.id === id);
      if (tribunalToUpdate) {
        // Actualiza el campo enviar_email_hito
        tribunalToUpdate.enviar_email_hito = isChecked;
        // Crear el objeto para la actualización
        const tribunalUpdateData: Tribunal = {
          id_usuario: tribunalToUpdate.id_usuario,
          enviar_email_hito: isChecked, // Nuevo valor del switch
          estado: tribunalToUpdate.estado,
        };
        // Llamar al servicio para actualizar en la base de datos
        this.participantesService.updateTribunal(id, tribunalUpdateData).subscribe({
          next: () => {
            //console.log(`Tribunal con ID ${id} actualizado con éxito. Estado enviar_email_hito: ${isChecked}`);
            // Puedes emitir eventos o recargar los datos si es necesario
            //this.refreshCase.emit();
          },
          error: (error) => {
            //console.error('Error updating tribunal:', error);
            this.error = error;
            this.loadTribunales();
          }
        });
      } else {
        console.error(`No se encontró el tribunal con ID ${id} en la lista.`);
      }
    } else {
      console.error(`No se encontró el switch con ID: switchHito_${id}`);
    }
  }



}
